/** @format */

import { Button, DatePicker, Popconfirm, Select } from "antd";
import moment, { Moment } from "moment";
import React, { useMemo, useState } from "react";
import {
  TPermessiPresenze,
  TPersonaPresenze,
  TPresenzaVista,
  TTurnoPresenze,
} from "../../../../redux-modules/declarations/maps/presenzeVista";
import { TShiftType } from "../../../../redux-modules/declarations/maps/shiftTypes";
import { maiuscola, modoCorrezione, ora, orario, TPulsanteAzione, TRATTINO } from "../format";
import { TModalitaPresenze } from "../../../../redux-modules/declarations/maps/turniSettimana";
import { ModuloInLinea, Pulsanti } from "../styles";
import { azioniPresenze, STATO_RICHIESTA, TEsitoAzione } from "../usePresenze";

/** Contesto comune alle azioni: la presenza o il turno su cui agire e il giorno mostrato. */
export interface TContestoAzioni {
  persona: TPersonaPresenze;
  presenza: TPresenzaVista | null;
  turno: TTurnoPresenze | null;
  /** giorno mostrato, per current_date delle chiamate esistenti */
  giorno: string;
  /** uscita proposta per "Registra l'uscita" */
  uscitaProposta?: string | null;
}

export interface TRisorseAzioni {
  permessi: TPermessiPresenze;
  /** con la banca ore disattivata i testi parlano di scostamento, non di banca ore */
  modalita: TModalitaPresenze;
  tipiTurno: TShiftType[];
  tipoPresenzaOrdinariaId: number;
  /** esito di ogni azione: notifica e rilettura le decide chi usa il componente */
  onEsito: (esito: TEsitoAzione) => void;
  /** apre "Nuova richiesta" con la persona, il giorno e, se c'è, l'orario del turno */
  onGiustificativo: (
    persona: TPersonaPresenze,
    data: string,
    intervallo?: [string, string],
  ) => void;
}

interface Props extends TContestoAzioni {
  pulsanti: TPulsanteAzione[];
  risorse: TRisorseAzioni;
  piccoli?: boolean;
}

const FORMATO = "DD/MM/YYYY HH:mm";
const daMoment = (m: Moment): string => m.format("YYYY-MM-DD HH:mm");
const aMoment = (dataOra: string | null | undefined): Moment | null =>
  dataOra ? moment(dataOra, "YYYY-MM-DD HH:mm") : null;

/** Pulsanti di una segnalazione o di una presenza, con i moduli in linea dove servono. */
export const PulsantiAzione = ({
  pulsanti,
  risorse,
  persona,
  presenza,
  turno,
  giorno,
  uscitaProposta,
  piccoli = true,
}: Props): JSX.Element | null => {
  const [aperto, setAperto] = useState<TPulsanteAzione | null>(null);
  const [inCorso, setInCorso] = useState<string | null>(null);

  if (!pulsanti.length) return null;

  const chiave = (p: TPulsanteAzione): string => `${p.tipo}:${p.codice || p.stato || ""}`;

  const esegui = (p: TPulsanteAzione, chiamata: () => Promise<TEsitoAzione>): Promise<boolean> => {
    setInCorso(chiave(p));
    return chiamata().then(esito => {
      setInCorso(null);
      if (esito.ok) setAperto(null);
      risorse.onEsito(esito);
      return esito.ok;
    });
  };

  const premi = (p: TPulsanteAzione): void => {
    switch (p.tipo) {
      case "timebank":
        if (presenza && p.codice) {
          const codice = p.codice;
          esegui(p, () => azioniPresenze.timebank(presenza, codice));
        }
        return;
      case "stato_richiesta":
        if (p.richiestaId && p.stato) {
          const richiestaId = p.richiestaId;
          const stato =
            p.stato === "accetta" ? STATO_RICHIESTA.accettata : STATO_RICHIESTA.rifiutata;
          esegui(p, () => azioniPresenze.statoRichiesta(richiestaId, persona.id, stato));
        }
        return;
      case "inserisci_giustificativo":
        risorse.onGiustificativo(
          persona,
          turno ? turno.data : giorno,
          turno ? [turno.inizio, turno.fine] : undefined,
        );
        return;
      case "elimina_presenza":
        return;
      default:
        setAperto(attuale => (attuale && chiave(attuale) === chiave(p) ? null : p));
    }
  };

  const pulsante = (p: TPulsanteAzione): JSX.Element => {
    const comune = {
      size: piccoli ? ("small" as const) : ("middle" as const),
      disabled: !!p.disattivato || (inCorso !== null && inCorso !== chiave(p)),
      loading: inCorso === chiave(p),
    };
    if (p.tipo === "elimina_presenza" && presenza) {
      return (
        <Popconfirm
          key={chiave(p)}
          title={`Eliminare la presenza ${ora(presenza.entrata)}${TRATTINO}${
            presenza.uscita ? ora(presenza.uscita) : "in corso"
          }?`}
          okText="Elimina"
          okButtonProps={{ danger: true }}
          cancelText="Annulla"
          onConfirm={(): Promise<boolean> =>
            esegui(p, () => azioniPresenze.eliminaPresenza(presenza))
          }
        >
          <Button {...comune} danger>
            {p.etichetta}
          </Button>
        </Popconfirm>
      );
    }
    return (
      <Button
        key={chiave(p)}
        {...comune}
        type={p.principale ? "primary" : "default"}
        danger={p.pericolosa}
        aria-expanded={
          p.tipo !== "timebank" &&
          p.tipo !== "inserisci_giustificativo" &&
          p.tipo !== "stato_richiesta"
            ? !!aperto && chiave(aperto) === chiave(p)
            : undefined
        }
        onClick={(): void => premi(p)}
      >
        {p.etichetta}
      </Button>
    );
  };

  const chiudi = (): void => setAperto(null);
  const ricalcolo = risorse.modalita === "banca_ore" ? "la banca ore" : "lo scostamento dal turno";
  const ricalcoloBreve = risorse.modalita === "banca_ore" ? "banca ore" : "scostamento dal turno";
  let modulo: JSX.Element | null = null;
  if (aperto && aperto.tipo === "inserisci_presenza" && turno) {
    modulo = (
      <ModuloOrari
        titolo={`Inserisci presenza per il turno ${orario(turno.ora_inizio, turno.ora_fine)}`}
        entrata={turno.inizio}
        uscita={turno.fine}
        spiegazione={`La presenza nasce già collegata al turno ${orario(
          turno.ora_inizio,
          turno.ora_fine,
        )}. ${maiuscola(ricalcoloBreve)} e segnalazioni si ricalcolano al salvataggio.`}
        salva="Salva presenza"
        onAnnulla={chiudi}
        onSalva={(entrata, uscita): Promise<boolean> =>
          esegui(aperto, () =>
            azioniPresenze.inserisciPresenza(
              persona.id,
              entrata,
              uscita,
              turno.tipo_turno_id,
              risorse.tipoPresenzaOrdinariaId,
            ),
          )
        }
      />
    );
  } else if (
    aperto &&
    (aperto.tipo === "correggi_orari" || aperto.tipo === "registra_uscita") &&
    presenza
  ) {
    const modo =
      aperto.tipo === "registra_uscita" ? "presenza" : modoCorrezione(presenza, risorse.permessi);
    if (modo) {
      modulo = (
        <ModuloOrari
          titolo={aperto.tipo === "registra_uscita" ? "Registra l'uscita" : "Correggi gli orari"}
          entrata={presenza.entrata}
          uscita={aperto.tipo === "registra_uscita" ? uscitaProposta || null : presenza.uscita}
          spiegazione={
            modo === "timebank"
              ? `Restano nello storico gli orari timbrati (${ora(
                  presenza.timbrata.entrata,
                )}${TRATTINO}${
                  presenza.timbrata.uscita ? ora(presenza.timbrata.uscita) : "in corso"
                }). ${maiuscola(ricalcolo)} si ricalcola al salvataggio.`
              : `Cambia gli orari della presenza. ${maiuscola(
                  ricalcoloBreve,
                )} e segnalazioni si ricalcolano al salvataggio.`
          }
          salva="Salva orari"
          onAnnulla={chiudi}
          onSalva={(entrata, uscita): Promise<boolean> =>
            esegui(aperto, () =>
              azioniPresenze.correggiOrari(presenza, modo, entrata, uscita, giorno),
            )
          }
        />
      );
    }
  } else if (
    aperto &&
    (aperto.tipo === "assegna_turno" || aperto.tipo === "cambia_turno") &&
    presenza
  ) {
    const cambia = aperto.tipo === "cambia_turno";
    modulo = (
      <ModuloTurno
        presenza={presenza}
        tipiTurno={risorse.tipiTurno}
        escludi={cambia && turno ? String(turno.tipo_turno_id) : null}
        salva={cambia ? "Cambia turno" : "Assegna turno"}
        spiegazione={
          cambia
            ? `Il turno collegato alla presenza cambia orario. ${maiuscola(
                ricalcolo,
              )} si ricalcola al salvataggio.`
            : "Il turno nasce collegato alla presenza; un turno dello stesso giorno senza timbrature viene sostituito."
        }
        onAnnulla={chiudi}
        onSalva={(tipoTurnoId): Promise<boolean> =>
          esegui(aperto, () =>
            cambia
              ? azioniPresenze.cambiaTurno(presenza, tipoTurnoId, giorno)
              : azioniPresenze.assegnaTurno(presenza, tipoTurnoId, giorno),
          )
        }
      />
    );
  }

  return (
    <>
      <Pulsanti>{pulsanti.map(pulsante)}</Pulsanti>
      {modulo}
    </>
  );
};

// ------------------------------------------------------------------ moduli in linea

interface PropsOrari {
  titolo: string;
  entrata: string;
  uscita: string | null;
  spiegazione: string;
  salva: string;
  onSalva: (entrata: string, uscita: string) => Promise<boolean>;
  onAnnulla: () => void;
}

/** Entrata e uscita con data e ora: una presenza può finire il giorno dopo. */
export const ModuloOrari = ({
  titolo,
  entrata,
  uscita,
  spiegazione,
  salva,
  onSalva,
  onAnnulla,
}: PropsOrari): JSX.Element => {
  const [da, setDa] = useState<Moment | null>(aMoment(entrata));
  const [a, setA] = useState<Moment | null>(aMoment(uscita));
  const [errore, setErrore] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const invia = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!da || !a) {
      setErrore("Indica entrata e uscita.");
      return;
    }
    if (!a.isAfter(da)) {
      setErrore("L'uscita deve essere dopo l'entrata.");
      return;
    }
    setErrore(null);
    setSalvando(true);
    onSalva(daMoment(da), daMoment(a)).then(() => setSalvando(false));
  };

  return (
    <ModuloInLinea aria-label={titolo} onSubmit={invia}>
      <div className="campi">
        <label>
          Entrata
          <DatePicker
            showTime={{ format: "HH:mm" }}
            format={FORMATO}
            allowClear={false}
            value={da}
            onChange={(v): void => setDa(v)}
          />
        </label>
        <label>
          Uscita
          <DatePicker
            showTime={{ format: "HH:mm" }}
            format={FORMATO}
            allowClear={false}
            value={a}
            placeholder="Scegli l'uscita"
            onChange={(v): void => setA(v)}
          />
        </label>
      </div>
      <p className="spiegazione">{spiegazione}</p>
      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}
      <Pulsanti>
        <Button size="small" type="primary" htmlType="submit" loading={salvando}>
          {salva}
        </Button>
        <Button size="small" type="text" onClick={onAnnulla}>
          Annulla
        </Button>
      </Pulsanti>
    </ModuloInLinea>
  );
};

interface PropsTurno {
  presenza: TPresenzaVista;
  tipiTurno: TShiftType[];
  escludi: string | null;
  salva: string;
  spiegazione: string;
  onSalva: (tipoTurnoId: string) => Promise<boolean>;
  onAnnulla: () => void;
}

const minutiOra = (hhmm: string): number => +hhmm.substr(0, 2) * 60 + +hhmm.substr(3, 2);

/** Scelta del turno, con in cima quelli che iniziano più vicino all'entrata timbrata. */
export const ModuloTurno = ({
  presenza,
  tipiTurno,
  escludi,
  salva,
  spiegazione,
  onSalva,
  onAnnulla,
}: PropsTurno): JSX.Element => {
  const entrata = minutiOra(ora(presenza.entrata));
  const opzioni = useMemo(
    () =>
      tipiTurno
        .filter(t => parseInt(t.working_minutes, 10) > 0 && String(t.id) !== escludi)
        .map(t => {
          const distanza = Math.abs(minutiOra(t.time_from || "00:00") - entrata);
          return { t, distanza: Math.min(distanza, 1440 - distanza) };
        })
        .sort((x, y) => x.distanza - y.distanza || x.t.name.localeCompare(y.t.name, "it"))
        .map(({ t }) => ({ value: String(t.id), label: t.name })),
    [tipiTurno, escludi, entrata],
  );
  const [scelto, setScelto] = useState<string | undefined>(opzioni[0]?.value);
  const [salvando, setSalvando] = useState(false);

  const invia = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!scelto) return;
    setSalvando(true);
    onSalva(scelto).then(() => setSalvando(false));
  };

  return (
    <ModuloInLinea aria-label={salva} onSubmit={invia}>
      <label>
        Turno
        <Select
          showSearch
          optionFilterProp="label"
          value={scelto}
          options={opzioni}
          placeholder="Scegli il turno"
          notFoundContent="Nessun turno"
          onChange={(v: string): void => setScelto(v)}
        />
      </label>
      <p className="spiegazione">{spiegazione}</p>
      <Pulsanti>
        <Button size="small" type="primary" htmlType="submit" loading={salvando} disabled={!scelto}>
          {salva}
        </Button>
        <Button size="small" type="text" onClick={onAnnulla}>
          Annulla
        </Button>
      </Pulsanti>
    </ModuloInLinea>
  );
};
